/**
 * Hook for handling pipeline save operations.
 * Provides handlers for Save, Save As, and structure-only saves.
 */

import { useCallback, useState } from 'react';
import { Node, Edge } from '@xyflow/react';
import { message } from 'antd';
import { createContextLogger } from '../../../../utils/logger';

const logger = createContextLogger('useCanvasSave');
import { buildPipelineSaveRequest, extractPipelineNameFromPath } from '../utils/pipelineBuilder';
import useTabManagerStore from '../../../../store/tabManagerStore';
import { apiClient } from '../../../../api/client';

interface UseCanvasSaveProps {
  pipelineId: string;
  nodes: Node[];
  edges: Edge[];
  tabType: 'pipeline' | 'workflow';
  hasStructuralChanges: boolean;
  getTransformState: (nodeId: string) => any;
  updateTabName: (id: string, name: string) => void;
  migrateTabId: (oldId: string, newId: string) => void;
  updateTabIsNew: (id: string, isNew: boolean) => void;
  migratePipelineId: (oldId: string, newId: string) => void;
  clearStructuralChanges: (pipelineId: string) => void;
}

export function useCanvasSave({
  pipelineId,
  nodes,
  edges,
  tabType,
  hasStructuralChanges,
  getTransformState,
  updateTabName,
  migrateTabId,
  updateTabIsNew,
  migratePipelineId,
  clearStructuralChanges,
}: UseCanvasSaveProps) {
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const { tabs, updateTabModified } = useTabManagerStore();

  /**
   * Save pipeline structure (for existing files with structural changes)
   * Process:
   * 1. Save structure to backend (backend handles renames)
   * 2. Update local node IDs to match backend state
   * 3. Save modified transform values using NEW names
   */
  const handleSaveStructure = useCallback(async () => {
    try {
      const { filesService } = await import('../../../../api/services/files');
      const { transformsService } = await import('../../../../api/services/transforms');
      
      if (nodes.length === 0) {
        message.error(`No ${tabType} to save`);
        return;
      }

      // Get current tab to extract file path
      const currentTab = tabs.find(tab => tab.id === pipelineId);
      if (!currentTab || currentTab.isNew) {
        logger.error('Cannot save structure: No file path for pipeline');
        message.error('Cannot save: No file path found');
        return;
      }

      const filePath = pipelineId; // For existing files, pipelineId IS the file path
      const filename = extractPipelineNameFromPath(filePath);
      
      // Build pipeline save request
      const pipelineStructure = buildPipelineSaveRequest(
        nodes,
        edges,
        filename,
        tabType
      );

      logger.info('Saving pipeline structure:', {
        filePath,
        filename,
        nodeCount: nodes.length,
        edgeCount: edges.length
      });

      // STEP 1: Save structure to backend
      if (tabType === 'workflow') {
        await filesService.saveWorkflowFile(filePath, pipelineStructure);
      } else {
        await filesService.savePipelineFile(filePath, pipelineStructure);
      }
      
      logger.info('✅ Pipeline structure saved to backend');
      
      // STEP 2: Update node IDs immediately to match backend state
      // The backend has now processed all renames, so we need to sync local state
      const store = await import('../../../../store/store');
      const state = store.useStore.getState();
      const activePipeline = state.pipelines.find(p => p.id === pipelineId);
      
      if (!activePipeline) {
        logger.error('Cannot find active pipeline in store');
        return;
      }

      let updatedNodes = [...activePipeline.nodes];
      let updatedEdges = [...activePipeline.edges];
      const nodeIdMappings: Array<{ oldId: string; newId: string }> = [];
      
      // Collect all nodes that need ID updates (where node.id !== node.data.name)
      for (const node of updatedNodes) {
        const currentName = node.data?.name || node.id;
        
        logger.debug('Checking node for ID update:', {
          nodeId: node.id,
          'node.data.name': node.data?.name,
          'node.data.label': node.data?.label,
          currentName,
          needsUpdate: node.id !== currentName,
          allDataKeys: Object.keys(node.data || {})
        });
        
        if (node.id !== currentName) {
          nodeIdMappings.push({ oldId: node.id, newId: currentName });
        }
      }
      
      // Update all node IDs at once if needed
      if (nodeIdMappings.length > 0) {
        logger.info('🔄 Updating node IDs after structure save:', nodeIdMappings);
        
        // Create a mapping object for quick lookup
        const idMap = new Map(nodeIdMappings.map(m => [m.oldId, m.newId]));
        
        // Update all node IDs
        for (const mapping of nodeIdMappings) {
          updatedNodes = updatedNodes.map(n =>
            n.id === mapping.oldId ? { ...n, id: mapping.newId } : n
          );
          
          // Migrate transform state from old ID to new ID
          const oldTransformState = state.getTransformState(mapping.oldId);
          if (oldTransformState) {
            state.initializeTransformState(mapping.newId, oldTransformState.isNew);
            state.updateTransformState(mapping.newId, {
              ...oldTransformState,
              source: 'file'
            });
          }
        }
        
        // Update ALL edges in one pass (handles cases where both source and target are renamed)
        updatedEdges = updatedEdges.map(edge => {
          const newSource = idMap.get(edge.source) || edge.source;
          const newTarget = idMap.get(edge.target) || edge.target;
          
          if (newSource !== edge.source || newTarget !== edge.target) {
            logger.debug('🔗 Updating edge after node rename:', {
              oldEdge: { id: edge.id, source: edge.source, target: edge.target },
              newEdge: { id: `${newSource}-${newTarget}`, source: newSource, target: newTarget }
            });
            return { ...edge, source: newSource, target: newTarget, id: `${newSource}-${newTarget}` };
          }
          return edge;
        });
        
        // Check if the selected node was renamed and update selectedNodeId
        const currentSelectedNodeId = activePipeline.selectedNodeId;
        let newSelectedNodeId = currentSelectedNodeId;
        if (currentSelectedNodeId) {
          const mapping = nodeIdMappings.find(m => m.oldId === currentSelectedNodeId);
          if (mapping) {
            newSelectedNodeId = mapping.newId;
            logger.info('📍 Updating selected node ID:', {
              oldId: currentSelectedNodeId,
              newId: newSelectedNodeId
            });
          }
        }
        
        // Clear originalName from all nodes' _state BEFORE applying updates
        for (const mapping of nodeIdMappings) {
          const node = updatedNodes.find(n => n.id === mapping.newId);
          if (node?.data?._state?.originalValues?.name) {
            logger.debug('🧹 Clearing originalName after structure save:', {
              nodeId: mapping.newId,
              originalName: node.data._state.originalValues.name
            });
            
            // Update the node data directly in updatedNodes
            const nodeIndex = updatedNodes.findIndex(n => n.id === mapping.newId);
            if (nodeIndex !== -1) {
              updatedNodes[nodeIndex] = {
                ...updatedNodes[nodeIndex],
                data: {
                  ...updatedNodes[nodeIndex].data,
                  _state: {
                    ...updatedNodes[nodeIndex].data?._state,
                    originalValues: {
                      ...updatedNodes[nodeIndex].data?._state?.originalValues,
                      name: undefined
                    }
                  }
                }
              };
            }
          }
        }
        
        // Apply all ID updates at once
        state.updatePipeline(pipelineId, {
          nodes: updatedNodes,
          edges: updatedEdges,
          selectedNodeId: newSelectedNodeId
        });
        logger.info('✅ All node IDs updated successfully');
      }
      
      // STEP 3: NOW save any modified transform values (using the NEW node IDs)
      const modifiedTransforms = updatedNodes.filter((node) => {
        const transformState = state.getTransformState(node.id);
        return transformState?.isModified === true;
      });

      logger.info(`Found ${modifiedTransforms.length} modified transforms to save`);

      if (modifiedTransforms.length > 0) {
        const savePromises = modifiedTransforms.map(async (node) => {
          try {
            const currentName = node.data?.name || node.id;
            
            // Extract transform values (excluding internal fields)
            const internalFields = ['_state', '_cachedValues', 'iconType', 'nodeType', 'label', 'svg', 'incomingFields', 'outgoingFields'];
            const transformValues: Record<string, any> = {};
            
            if (node.data) {
              Object.keys(node.data).forEach((key) => {
                if (!internalFields.includes(key)) {
                  transformValues[key] = node.data[key];
                }
              });
            }
            
            // Create ActionTransformValues structure
            const actionTransformValues = {
              id: node.id,
              type: node.data?.type || 'Unknown',
              xLocation: Math.round(node.position.x),
              yLocation: Math.round(node.position.y),
              values: transformValues
            };

            logger.debug('Saving modified transform values:', {
              nodeId: node.id,
              currentName,
              type: node.data?.type
            });

            // Save to backend using current name (IDs are already synced)
            await transformsService.postTransform(currentName, pipelineId, actionTransformValues);
            
            // Clear modified flag
            state.updateTransformState(node.id, {
              isModified: false,
              source: 'file'
            });
            
            return { success: true, nodeId: node.id };
          } catch (error: any) {
            const nodeName = node.data?.name || node.id;
            logger.error(`Failed to save transform ${nodeName}:`, error);
            return { success: false, nodeId: node.id, error };
          }
        });

        const results = await Promise.all(savePromises);
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        if (failCount > 0) {
          logger.warn(`Saved ${successCount} transform(s), failed ${failCount}`);
        } else {
          logger.info(`✅ Saved ${successCount} modified transform value(s)`);
        }
      }
      
      // Clear structural changes flag
      clearStructuralChanges(pipelineId);
      
      // Mark tab as unmodified
      updateTabModified(pipelineId, false);

      // Clear API cache for this specific pipeline
      apiClient.clearPipelineCache(pipelineId, tabType);

      const typeCapitalized = tabType.charAt(0).toUpperCase() + tabType.slice(1);
      message.success(`${typeCapitalized} saved successfully`);
      
    } catch (error: any) {
      logger.error('Failed to save pipeline structure:', error);
      message.error(`Failed to save structure: ${error.message}`);
    }
  }, [nodes, edges, tabType, pipelineId, tabs, getTransformState, clearStructuralChanges, updateTabModified]);

  /**
   * Save modified transforms to backend (without structural changes)
   */
  const handleSave = useCallback(async () => {
    try {
      const { transformsService } = await import('../../../../api/services/transforms');
      
      // Find all modified transforms
      const modifiedTransforms = nodes.filter((node) => {
        const transformState = getTransformState(node.id);
        return transformState?.isModified === true;
      });

      logger.info('Saving pipeline with modified transforms:', {
        pipelineId,
        modifiedCount: modifiedTransforms.length,
        totalNodes: nodes.length
      });

      // Save each modified transform
      const savePromises = modifiedTransforms.map(async (node) => {
        try {
          const currentName = node.data?.name || node.id;
          const originalName = node.data?._state?.originalValues?.name;
          
          // Extract transform values (excluding internal fields)
          const internalFields = ['_state', '_cachedValues', 'iconType', 'nodeType', 'label', 'svg', 'incomingFields', 'outgoingFields'];
          const transformValues: Record<string, any> = {};
          
          if (node.data) {
            Object.keys(node.data).forEach((key) => {
              if (!internalFields.includes(key)) {
                transformValues[key] = node.data[key];
              }
            });
          }
          
          // Create ActionTransformValues structure
          const actionTransformValues = {
            id: node.id,
            type: node.data?.type || 'Unknown',
            xLocation: Math.round(node.position.x),
            yLocation: Math.round(node.position.y),
            values: transformValues
          };

          const transformName = originalName || currentName;
          
          logger.debug('Saving transform:', {
            nodeId: node.id,
            transformName,
            currentName,
            originalName,
            type: node.data?.type,
            position: node.position,
            dataKeys: Object.keys(node.data || {})
          });

          await transformsService.postTransform(transformName, pipelineId, actionTransformValues);
          
          return { 
            success: true, 
            nodeId: node.id,
            oldNodeId: node.id,
            newNodeId: currentName,
            needsIdUpdate: node.id !== currentName,
            hadOriginalName: !!originalName
          };
        } catch (error: any) {
          const nodeName = node.data?.name || node.id;
          logger.error(`Failed to save transform ${nodeName}:`, error);
          message.error(`Failed to save ${nodeName}: ${error.message}`);
          return { success: false, nodeId: node.id, error };
        }
      });

      const results = await Promise.all(savePromises);
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      // Now update node IDs for successfully saved transforms (after all saves complete)
      const store = await import('../../../../store/store');
      const state = store.useStore.getState();
      const activePipeline = state.pipelines.find(p => p.id === pipelineId);
      
      if (activePipeline) {
        let updatedNodes = [...activePipeline.nodes];
        let updatedEdges = [...activePipeline.edges];
        
        // Process each successful save that needs an ID update
        for (const result of results) {
          if (result.success && result.needsIdUpdate) {
            const oldId = result.oldNodeId;
            const newId = result.newNodeId;
            
            logger.info('🔄 Updating node ID after successful save:', {
              oldId,
              newId
            });
            
            // Update the node ID
            updatedNodes = updatedNodes.map(n =>
              n.id === oldId ? { ...n, id: newId } : n
            );
            
            // Update edges to reference new ID
            updatedEdges = updatedEdges.map(edge => {
              if (edge.source === oldId && edge.target === oldId) {
                return { ...edge, source: newId, target: newId, id: `${newId}-${newId}` };
              } else if (edge.source === oldId) {
                return { ...edge, source: newId, id: `${newId}-${edge.target}` };
              } else if (edge.target === oldId) {
                return { ...edge, target: newId, id: `${edge.source}-${newId}` };
              }
              return edge;
            });
            
            // Migrate transform state from old ID to new ID
            const oldTransformState = state.getTransformState(oldId);
            if (oldTransformState) {
              state.initializeTransformState(newId, oldTransformState.isNew);
              state.updateTransformState(newId, {
                isModified: false,
                source: 'file',
                cacheValid: oldTransformState.cacheValid,
                lastFetched: oldTransformState.lastFetched
              });
            }
          } else if (result.success) {
            // Just clear modified flag for transforms that didn't need ID update
            state.updateTransformState(result.nodeId, {
              isModified: false,
              source: 'file'
            });
          }
        }
        
        // Apply all updates at once
        if (results.some(r => r.success && r.needsIdUpdate)) {
          state.updatePipeline(pipelineId, {
            nodes: updatedNodes,
            edges: updatedEdges
          });
          logger.info('✅ All node IDs updated successfully after saves');
        }
        
        // Clear originalName from _state for all successfully saved transforms
        for (const result of results) {
          if (result.success && result.hadOriginalName) {
            const nodeId = result.needsIdUpdate ? result.newNodeId : result.nodeId;
            const node = updatedNodes.find(n => n.id === nodeId);
            
            if (node?.data?._state?.originalValues?.name) {
              logger.debug('🧹 Clearing originalName after successful save:', {
                nodeId,
                originalName: node.data._state.originalValues.name
              });
              
              state.updateNodeData(nodeId, {
                ...node.data,
                _state: {
                  ...node.data._state,
                  originalValues: {
                    ...node.data._state.originalValues,
                    name: undefined
                  }
                }
              });
            }
          }
        }
      }

      if (failCount === 0 && successCount > 0) {
        message.success(`Saved ${successCount} transform${successCount > 1 ? 's' : ''}`);
        
        // Clear API cache for this specific pipeline (only on successful save)
        apiClient.clearPipelineCache(pipelineId, tabType);
      } else if (failCount > 0) {
        message.warning(`Saved ${successCount}, failed ${failCount}`);
      }

      // Mark tab as unmodified
      updateTabModified(pipelineId, false);

    } catch (error: any) {
      logger.error('Failed to save pipeline:', error);
      message.error(`Failed to save: ${error.message}`);
    }
  }, [pipelineId, nodes, getTransformState, updateTabModified]);

  /**
   * Save or Save As handler (checks if file is new or has structural changes)
   */
  const handleSaveFile = useCallback(async () => {
    const currentTab = tabs.find(tab => tab.id === pipelineId);
    
    // If new file, open file browser
    if (currentTab?.isNew) {
      setSaveAsDialogOpen(true);
      return;
    }
    
    logger.info('handleSaveFile routing decision:', {
      pipelineId,
      hasStructuralChanges,
      currentTab: currentTab ? { id: currentTab.id, name: currentTab.name, isNew: currentTab.isNew } : null
    });
    
    // If has structural changes, save structure
    if (hasStructuralChanges) {
      logger.info('Routing to handleSaveStructure (structural changes detected)');
      await handleSaveStructure();
      return;
    }
    
    // Otherwise, save only modified transforms
    logger.info('Routing to handleSave (no structural changes, only modified values)');
    await handleSave();
  }, [pipelineId, tabs, hasStructuralChanges, handleSaveStructure, handleSave]);

  /**
   * Save As handler - saves entire pipeline structure
   */
  const handleSaveAs = useCallback(async (path: string): Promise<void> => {
    try {
      const { filesService } = await import('../../../../api/services/files');
      
      if (nodes.length === 0) {
        message.error(`No ${tabType} to save`);
        return;
      }

      // Build pipeline save request
      const filename = extractPipelineNameFromPath(path);
      const pipelineStructure = buildPipelineSaveRequest(
        nodes,
        edges,
        filename,
        tabType
      );

      // Save to backend
      if (tabType === 'workflow') {
        await filesService.saveWorkflowFile(path, pipelineStructure);
      } else {
        await filesService.savePipelineFile(path, pipelineStructure);
      }
      
      // Update tab metadata
      const displayName = filename;
      const typeCapitalized = tabType.charAt(0).toUpperCase() + tabType.slice(1);
      
      const currentTab = tabs.find(tab => tab.id === pipelineId);
      const isNewFile = currentTab?.isNew === true;
      
      if (isNewFile) {
        // Migrate tab and pipeline IDs for new files
        migrateTabId(pipelineId, path);
        migratePipelineId(pipelineId, path);
        updateTabName(path, displayName);
        updateTabIsNew(path, false);
        updateTabModified(path, false);
        
        logger.info('Migrated new file to saved file:', {
          oldId: pipelineId,
          newId: path,
          filename
        });
      } else {
        // Just update the existing tab
        updateTabModified(pipelineId, false);
      }
      
      // Clear structural changes flag
      clearStructuralChanges(isNewFile ? path : pipelineId);
      
      // Clear API cache for the saved pipeline (use new path for new files)
      const savedPath = isNewFile ? path : pipelineId;
      apiClient.clearPipelineCache(savedPath, tabType);
      
      // Trigger file browser refresh to show the new file
      window.dispatchEvent(new CustomEvent('refreshFileBrowser'));
      
      message.success(`${typeCapitalized} saved as ${displayName}`);
      
    } catch (error: any) {
      logger.error('Failed to save as:', error);
      message.error(`Failed to save: ${error.message}`);
      throw error;
    }
  }, [nodes, edges, tabType, pipelineId, tabs, updateTabModified, migrateTabId, migratePipelineId, updateTabName, updateTabIsNew, clearStructuralChanges]);

  return {
    handleSave,
    handleSaveFile,
    handleSaveAs,
    handleSaveStructure,
    saveAsDialogOpen,
    setSaveAsDialogOpen,
  };
}
